import { FunctionalComponent, h } from '@stencil/core';
import { InceptionNet } from './card-data-points-components/inceptionNet';
import { Nav } from './card-data-points-components/nav';
import { Net } from './card-data-points-components/net';
import { Assets } from './card-data-points-components/assets';
import { Carbs } from './card-data-points-components/carbs';
import { InceptionNav } from './card-data-points-components/inceptionNav';

import { FundData } from '../../_interfaces/fund-card/fund-data';
import { FundDataPoints } from '../../_interfaces/fund-card/fund-data-points';
import { EsgVisibilityStyle } from '../../_types/fund-card-api';

import {
	getEsgBasedOnHoldingsDate,
	handleIfMissingPercentage,
	numberWithCommas,
} from './_helpers';

interface DataPointsProps {
	esgVisibility: EsgVisibilityStyle;
	data: FundData;
	Tooltip: string;
	i?: number;
}

export const DataPoints: FunctionalComponent<DataPointsProps> = ({
	esgVisibility,
	data,
	Tooltip,
	i = 0,
}) => {
	const {
		activeDataPoints,
		fundheader,
		keyfundfacts,
		esgAnalytics,
		performanceLabel,
		contentTranslations,
	} = data;

	const {
		labelNetAssetValue,
		labelYtdNavReturn,
		infobubbleYtd,
		infobubbleTotalReturnsDisclaimer,
		labelNavAmountChange,
		labelDataAsOf,
		labelHoldingsAsOf,
		navAsOfLabel,
	} = contentTranslations;

	// nav
	const navAmount = fundheader?.fundNav?.navAmount?.formattedValue;
	const navAsOfDate = fundheader?.fundNav?.navAmount?.formattedAsOfDate || '-';
	const fundCurrencyCode = fundheader?.fundNav?.navAmount?.prefix;

	// ytd
	// yearToDate formatted values sometimes contain a % sign and sometime not
	const ytdObj = fundheader?.fundNav?.yearToDate;
	const ytd = handleIfMissingPercentage(ytdObj?.formattedValue);
	const ytdAsOfDate = ytdObj?.formattedAsOfDate || '-';

	// nav change
	let navAmountChange = fundheader?.fundNav?.percentChange?.formattedValue;
	navAmountChange = handleIfMissingPercentage(navAmountChange);

	// assets
	const totalNetAssets = numberWithCommas(
		keyfundfacts?.totalNetAssets?.value.toFixed(2)
	);
	const labelTotalNetAssets = keyfundfacts?.totalNetAssets?.label;

	// inception
	const inceptionDate = keyfundfacts?.inceptionDate?.formattedValue || '-';
	const labelInceptionDate = keyfundfacts?.inceptionDate?.label;
	const fundCurrencySymbol = keyfundfacts?.totalNetAssets?.prefix;

	// carbs
	const wtavgCarbCoverage = esgAnalytics?.wtavgCarbCoverage?.formattedValue;
	const wtavgCarbCoverageLabel = esgAnalytics?.wtavgCarbCoverage?.label;
	const wtavgCarbCoverageTooltip = esgAnalytics?.wtavgCarbCoverage?.infoBubble;
	const wtavgCarbCoverageDate =
		esgAnalytics?.wtavgCarbCoverage?.formattedAsOfDate;
	const msciCarbonIntensity =
		esgAnalytics?.emeaWeightedAverageCarbon?.formattedValue;
	const msciCarbonIntensityDate =
		esgAnalytics?.emeaWeightedAverageCarbon?.formattedAsOfDate;
	const msciCarbonIntensityLabel =
		esgAnalytics?.emeaWeightedAverageCarbon?.label;
	const msciCarbonIntensityTooltip =
		esgAnalytics?.emeaWeightedAverageCarbon?.infoBubble;
	const basedOnHoldingsDateESG = getEsgBasedOnHoldingsDate(esgAnalytics);

	const dataPoints = {
		nav: {
			general: navAmount,
			label: labelNetAssetValue,
			tooltip: {
				first: `${navAsOfLabel} ${navAsOfDate}`,
			},
			data: `${fundCurrencyCode} ${navAmount}`,
			secondaryGeneral: ytd,
			secondaryLabel: labelYtdNavReturn,
			secondaryTooltip: {
				first: `${navAsOfLabel} ${ytdAsOfDate}`,
				second: `${infobubbleYtd} ${infobubbleTotalReturnsDisclaimer}`,
			},
			secondaryData: ytd,
		},
		net: {
			general: navAmount,
			label: labelNetAssetValue,
			tooltip: {
				first: `${navAsOfLabel} ${navAsOfDate}`,
			},
			data: `${fundCurrencyCode} ${navAmount}`,
			secondaryGeneral: navAmountChange,
			secondaryLabel: labelNavAmountChange,
			secondaryData: navAmountChange,
		},
		assets: {
			general: totalNetAssets,
			label: labelTotalNetAssets,
			tooltip: {
				first: `${navAsOfLabel} ${navAsOfDate}`,
			},
			data: `${fundCurrencySymbol} ${totalNetAssets}`,
			secondaryGeneral: ytd,
			secondaryLabel: labelYtdNavReturn,
			secondaryTooltip: {
				first: `${navAsOfLabel} ${ytdAsOfDate}`,
				second: `${infobubbleYtd} ${infobubbleTotalReturnsDisclaimer}`,
			},
			secondaryData: ytd,
		},
		inceptionNet: {
			general: inceptionDate,
			label: labelInceptionDate,
			data: inceptionDate,
			secondaryGeneral: totalNetAssets,
			secondaryLabel: labelTotalNetAssets,
			secondaryTooltip: {
				first: `${navAsOfLabel} ${navAsOfDate}`,
			},
			secondaryData: `${fundCurrencySymbol} ${totalNetAssets}`,
		},
		inceptionNav: {
			general: inceptionDate,
			label: labelInceptionDate,
			data: inceptionDate,
			secondaryGeneral: ytd,
			secondaryLabel: labelYtdNavReturn,
			secondaryTooltip: {
				first: `${navAsOfLabel} ${ytdAsOfDate}`,
				second: `${infobubbleYtd} ${infobubbleTotalReturnsDisclaimer}`,
			},
			secondaryData: ytd,
		},
		carbs: {
			general: wtavgCarbCoverage,
			label: wtavgCarbCoverageLabel,
			data: `${wtavgCarbCoverage}`,
			tooltip: {
				first: `${wtavgCarbCoverageTooltip}`,
				second: `${labelDataAsOf} ${wtavgCarbCoverageDate}`,
				third: `${labelHoldingsAsOf} ${basedOnHoldingsDateESG}`,
			},
			secondaryGeneral: msciCarbonIntensity,
			secondaryLabel: msciCarbonIntensityLabel,
			secondaryTooltip: {
				first: `${msciCarbonIntensityTooltip}`,
				second: `${labelDataAsOf} ${msciCarbonIntensityDate}`,
				third: `${labelHoldingsAsOf} ${basedOnHoldingsDateESG}`,
			},
			secondaryData: msciCarbonIntensity,
		},
	};
	const dataPointValues: FundDataPoints = dataPoints[activeDataPoints];

	return (
		<div class="ds-card-container ds-card-data-container">
			<h4
				class={`ds-eyebrow-s ds-data-label ds-data-label-${i} ds-performance-label`}
				style={{ display: esgVisibility }}
			>
				{performanceLabel}
			</h4>

			<Nav
				flag={activeDataPoints === 'nav'}
				dataPointValues={dataPointValues}
				Tooltip={Tooltip}
			/>

			<Net
				flag={activeDataPoints === 'net'}
				dataPointValues={dataPointValues}
				Tooltip={Tooltip}
			/>

			<Assets
				flag={activeDataPoints === 'assets'}
				dataPointValues={dataPointValues}
				Tooltip={Tooltip}
			/>

			<Carbs
				flag={activeDataPoints === 'carbs'}
				dataPointValues={dataPointValues}
				Tooltip={Tooltip}
			/>

			<InceptionNav
				flag={activeDataPoints === 'inceptionNav'}
				dataPointValues={dataPointValues}
				Tooltip={Tooltip}
			/>

			<InceptionNet
				flag={activeDataPoints === 'inceptionNet'}
				dataPointValues={dataPointValues}
				Tooltip={Tooltip}
			/>
		</div>
	);
};
